import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { sha256Hex } from "@/lib/utils/hash";

export type MedicalRecordRow = Database["public"]["Tables"]["medical_records"]["Row"];
export type ProntuarioRow = Database["public"]["Tables"]["prontuarios"]["Row"];
export type ProntuarioVersaoRow = Database["public"]["Tables"]["prontuario_versoes"]["Row"];

const toPlainJson = (value: unknown) => JSON.parse(JSON.stringify(value));

export const buildSnapshotFromMedicalRecord = (record: MedicalRecordRow) =>
  toPlainJson({
    ...record,
    // Não guardar campos voláteis no hash
    created_at: record.created_at,
    updated_at: record.updated_at,
    deleted_at: record.deleted_at,
  });

export async function ensureProntuarioForRecord(
  record: MedicalRecordRow,
  userId: string,
) {
  const { data: existing } = await supabase
    .from("prontuarios")
    .select("*")
    .eq("medical_record_id", record.id)
    .maybeSingle();

  if (existing) return existing as ProntuarioRow;

  const { data, error } = await supabase
    .from("prontuarios")
    .insert({
      medical_record_id: record.id,
      pet_id: record.pet_id,
      clinic_id: record.clinic_id,
      appointment_id: record.appointment_id,
      created_by: userId,
    })
    .select()
    .single();

  if (error) throw error;
  return data as ProntuarioRow;
}

export async function getLatestVersion(prontuarioId: string) {
  const { data, error } = await supabase
    .from("prontuario_versoes")
    .select("*")
    .eq("prontuario_id", prontuarioId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data as ProntuarioVersaoRow | null;
}

export async function appendProntuarioVersion(params: {
  prontuarioId: string;
  autorId: string;
  snapshot: unknown;
  acao: "CREATE" | "UPDATE" | "ADENDO";
  tipo: "NORMAL" | "ADENDO";
  ip?: string | null;
  userAgent?: string | null;
  metadata?: Record<string, unknown>;
}) {
  const { prontuarioId, autorId, snapshot, acao, tipo, ip, userAgent, metadata } = params;
  const latest = await getLatestVersion(prontuarioId);
  const payload = {
    snapshot: toPlainJson(snapshot),
    metadata: { ...(metadata ?? {}), acao, autorId },
    previous: latest?.hash_conteudo ?? null,
  };

  const hash = await sha256Hex(JSON.stringify(payload));

  const { data, error } = await supabase
    .from("prontuario_versoes")
    .insert({
      prontuario_id: prontuarioId,
      autor_id: autorId,
      conteudo_snapshot: payload.snapshot,
      acao,
      tipo,
      hash_anterior: latest?.hash_conteudo ?? null,
      hash_conteudo: hash,
      ip: ip ?? null,
      user_agent: userAgent ?? null,
      metadata: payload.metadata,
    })
    .select()
    .single();

  if (error) throw error;
  return data as ProntuarioVersaoRow;
}
